import { useState, useEffect, useCallback } from 'react';
import { useNotify } from 'react-admin';
import {
  Box, Typography, Card, CardContent, Stack, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, IconButton, Checkbox, Chip,
  CircularProgress, Collapse,
} from '@mui/material';
import AddIcon    from '@mui/icons-material/Add';
import CloseIcon  from '@mui/icons-material/Close';
import SaveIcon   from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import authFetch  from '../utils/authFetch';
import { NAVY, btn } from '../styles/ds';

export const ACTIVITY_TYPE_HE = {
  note:     { label: 'הערה',   bg: '#E2E3E5', color: '#383D41' },
  call:     { label: 'שיחה',   bg: '#CCE5FF', color: '#004085' },
  meeting:  { label: 'פגישה',  bg: '#D1ECF1', color: '#0C5460' },
  task:     { label: 'משימה',  bg: '#FFF3CD', color: '#856404' },
  reminder: { label: 'תזכורת', bg: '#F8D7DA', color: '#721C24' },
  system:   { label: 'מערכת',  bg: 'rgba(200,169,110,0.25)', color: '#6b5322' },
};

const WITH_DUE = new Set(['task', 'reminder']);
const fmtDateTime = iso => iso ? new Date(iso).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' }) : '';
const EMPTY = { activity_type: 'note', title: '', content: '', due_date: '' };

/**
 * Activity timeline + add form for a customer (customerId) or lead (leadId):
 * notes, calls, meetings, and tasks/reminders with a due date and a done
 * checkbox. Follow-up = task/reminder with a due date.
 */
const ActivityPanel = ({ customerId, leadId, isAdmin = false }) => {
  const notify = useNotify();
  const [items, setItems]     = useState(null);
  const [adding, setAdding]   = useState(false);
  const [fields, setFields]   = useState(EMPTY);
  const [saving, setSaving]   = useState(false);

  const filter = customerId ? { customer_id: customerId } : { lead_id: leadId };

  const load = useCallback(() => {
    authFetch(`/activities?filter=${encodeURIComponent(JSON.stringify(filter))}&_start=0&_end=100&_sort=created_at&_order=DESC`)
      .then(r => r.json())
      .then(d => setItems(Array.isArray(d) ? d : []))
      .catch(() => setItems([]));
  }, [customerId, leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (customerId || leadId) load(); }, [load, customerId, leadId]);

  const set = (k, v) => setFields(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!fields.title.trim()) { notify('נא להזין כותרת', { type: 'warning' }); return; }
    setSaving(true);
    try {
      const res = await authFetch('/activities', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...filter,
          activity_type: fields.activity_type,
          title: fields.title.trim(),
          content: fields.content || null,
          due_date: WITH_DUE.has(fields.activity_type) && fields.due_date ? fields.due_date : null,
        }),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error); }
      setFields(EMPTY); setAdding(false); load();
    } catch (e) { notify(e.message || 'שגיאה בשמירה', { type: 'error' }); }
    finally { setSaving(false); }
  };

  const toggleDone = async (a) => {
    await authFetch(`/activities/${a.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ done: !a.done }),
    });
    load();
  };

  const remove = async (a) => {
    const res = await authFetch(`/activities/${a.id}`, { method: 'DELETE' });
    if (!res.ok) { const b = await res.json().catch(() => ({})); notify(b.error || 'שגיאה', { type: 'error' }); return; }
    load();
  };

  const overdue = a => a.due_date && !a.done && new Date(a.due_date) < new Date();

  return (
    <Card sx={{ borderRadius: 2, mb: 3 }}>
      <CardContent className="crm-card-content" sx={{ p: 3 }}>
        <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography fontWeight={700} color="primary.main">פעילויות ומשימות</Typography>
          <Button size="small" variant={adding ? 'outlined' : 'contained'}
            startIcon={adding ? <CloseIcon /> : <AddIcon />}
            onClick={() => setAdding(o => !o)}
            sx={adding ? btn.gold
                       : { backgroundColor: NAVY, borderRadius: 2, '&:hover': { backgroundColor: '#162d5a' } }}>
            {adding ? 'סגור' : 'הוסף'}
          </Button>
        </Stack>

        <Collapse in={adding} timeout={200} unmountOnExit>
          <Box sx={{ p: 2, mb: 2, borderRadius: 2, backgroundColor: 'rgba(13,29,62,0.03)' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, gap: 2, mb: 2 }}>
              <FormControl size="small">
                <InputLabel>סוג</InputLabel>
                <Select value={fields.activity_type} label="סוג" onChange={e => set('activity_type', e.target.value)}>
                  {Object.entries(ACTIVITY_TYPE_HE).filter(([k]) => k !== 'system').map(([k, v]) =>
                    <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" label="כותרת" value={fields.title} onChange={e => set('title', e.target.value)} fullWidth />
            </Box>
            {WITH_DUE.has(fields.activity_type) && (
              <TextField size="small" type="datetime-local" label="תאריך יעד (למעקב)"
                value={fields.due_date} onChange={e => set('due_date', e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ mb: 2, width: 260 }} />
            )}
            <TextField size="small" label="פירוט" value={fields.content} onChange={e => set('content', e.target.value)}
              multiline rows={2} fullWidth sx={{ mb: 2 }} />
            <Button size="small" variant="contained" onClick={handleSave} disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              sx={{ backgroundColor: NAVY, borderRadius: 2 }}>
              שמור
            </Button>
          </Box>
        </Collapse>

        {items === null ? (
          <Typography fontSize={13} color="text.secondary">טוען...</Typography>
        ) : items.length === 0 ? (
          <Typography fontSize={13} color="text.secondary">אין פעילויות עדיין — הוסיפי הערה, שיחה או משימת מעקב</Typography>
        ) : (
          <Stack spacing={1.2}>
            {items.map(a => {
              const meta = ACTIVITY_TYPE_HE[a.activity_type] || ACTIVITY_TYPE_HE.note;
              return (
                <Box key={a.id} sx={{
                  p: 1.5, borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)',
                  backgroundColor: overdue(a) ? 'rgba(248,215,218,0.25)' : '#fff',
                }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    {WITH_DUE.has(a.activity_type) && (
                      <Checkbox size="small" checked={a.done} onChange={() => toggleDone(a)} sx={{ p: 0.3 }} />
                    )}
                    <Box flex={1}>
                      <Stack className="crm-wrap" direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip label={meta.label} size="small"
                          sx={{ height: 20, fontSize: 10, fontWeight: 700, backgroundColor: meta.bg, color: meta.color }} />
                        <Typography fontSize={14} fontWeight={600}
                          sx={a.done ? { textDecoration: 'line-through', color: 'text.secondary' } : {}}>
                          {a.title}
                        </Typography>
                        {a.due_date && (
                          <Typography fontSize={11} color={overdue(a) ? '#c62828' : 'text.secondary'} fontWeight={overdue(a) ? 700 : 400}>
                            יעד: {fmtDateTime(a.due_date)}
                          </Typography>
                        )}
                      </Stack>
                      {a.content && <Typography fontSize={12} color="text.secondary" mt={0.5} whiteSpace="pre-wrap">{a.content}</Typography>}
                      <Typography fontSize={11} color="text.secondary" mt={0.5}>
                        {fmtDateTime(a.created_at)}{a.created_by_username ? ` · ${a.created_by_username}` : ''}
                      </Typography>
                    </Box>
                    {isAdmin && a.activity_type !== 'system' && (
                      <IconButton size="small" onClick={() => remove(a)} aria-label="מחיקת פעילות" sx={{ color: '#c62828' }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityPanel;
